import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem,
} from '@mui/material'
import {
  updateActionRoutine,
  selectMutationLoading,
} from 'features/actions/ducks'

const SEVERITIES = [
  { value: 'critical', label: 'Critical' },
  { value: 'high',     label: 'High' },
  { value: 'medium',   label: 'Medium' },
  { value: 'low',      label: 'Low' },
]

const dialogPaperSx = {
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
}

const dialogTitleSx = {
  fontSize: '18px',
  fontWeight: 600,
  color: '#0C1229',
  padding: '20px 24px',
  borderBottom: '1px solid #f2f2f2',
}

const dialogContentSx = {
  padding: '20px 24px !important',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}

const dialogActionsSx = {
  padding: '16px 24px',
  borderTop: '1px solid #f2f2f2',
  gap: '8px',
}

const cancelButtonSx = {
  textTransform: 'none',
  fontWeight: 500,
  color: '#0C1229',
  borderColor: '#f2f2f2',
  borderRadius: '8px',
  padding: '8px 20px',
  '&:hover': {
    backgroundColor: '#f2f2f2',
    borderColor: '#0C1229',
  },
}

const submitButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '15px',
  minHeight: '40px',
  backgroundColor: '#33becc',
  color: '#0C1229',
  borderRadius: '8px',
  padding: '8px 24px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#2aa6b3',
    boxShadow: 'none',
  },
}

const EditActionDialog = ({ action, open, onClose, zIndex }) => {
  const dispatch = useDispatch()
  const submitting = useSelector(selectMutationLoading)
  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState('medium')
  const [description, setDescription] = useState('')
  const [dueBy, setDueBy] = useState('')

  useEffect(() => {
    if (action && open) {
      setTitle(action.title || '')
      setSeverity(action.severity || 'medium')
      setDescription(action.description || '')
      setDueBy(action.dueDate || '')
    }
  }, [action, open])

  if (!action) return null

  const closed = ['resolved', 'dismissed'].includes(action.stage) || ['resolved', 'dismissed'].includes(action.status)

  const submit = () => {
    const payload = { id: action.id }
    if (title !== action.title) payload.title = title
    if (severity !== action.severity) payload.severity = severity
    if ((description || '') !== (action.description || '')) payload.description = description || null
    if (!closed && dueBy && dueBy !== (action.dueDate || '')) payload.dueDate = dueBy
    if (Object.keys(payload).length === 1) {
      onClose?.()
      return
    }
    dispatch(updateActionRoutine(payload))
    onClose?.()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperSx }}
      sx={zIndex ? { zIndex } : undefined}
    >
      <DialogTitle sx={dialogTitleSx}>Edit remedial</DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <TextField
          label="Title"
          required
          fullWidth
          size="small"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />
        <TextField
          label="Severity"
          select
          required
          fullWidth
          size="small"
          value={severity}
          onChange={e => setSeverity(e.target.value)}
          helperText={
            action.source === 'guru' && severity !== action.severity
              ? `Guru originally extracted this as ${SEVERITIES.find(s => s.value === action.severity)?.label || action.severity}`
              : undefined
          }
        >
          {SEVERITIES.map(s => (
            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Description"
          multiline
          rows={3}
          fullWidth
          size="small"
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
        <TextField
          label="Due date"
          type="date"
          required={!closed}
          disabled={closed}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dueBy}
          onChange={e => setDueBy(e.target.value)}
          helperText={
            closed
              ? `This remedial is ${action.stage === 'dismissed' || action.status === 'dismissed' ? 'dismissed' : 'resolved'}, so its due date can no longer be changed.`
              : ['task_created', 'task_in_progress'].includes(action?.stage)
              ? "A task is handling this remedial, so the task's deadline decides when it is overdue. This date is used again if the task is removed."
              : 'The date this remedial must be put right by. It counts as overdue from the day after.'
          }
        />
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} variant="outlined" sx={cancelButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting || !title.trim() || (!closed && !dueBy)}
          sx={submitButtonSx}
        >
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditActionDialog
